import React, { Component } from 'react';
import _ from "lodash";
import { connect } from "react-redux";
import { api } from "@redux/api";
import { Col, Collapse, Divider, Button, Modal, Select, message, Avatar, Typography, Checkbox, Popconfirm } from "antd";
import { Spinner } from "@components/Spinner";
import EditableTable from "@components/Table/EditableTable";
import EditableTableActions from "@redux/EditableTable/actions";
import moment from "moment";
import { UserOutlined } from '@ant-design/icons';
import AssignButton from './AssignButton';
import { useKeepAliveContext } from '@/hooks/use-keep-alive-context';
import { EventName, useEventBus } from '@/hooks/useEventBus';

const { Panel } = Collapse;
const { Option } = Select;
const { Text } = Typography;



// 定义表格属性
const CertifyRequestProps = {
    'id': {
        title: 'ID',
        dataIndex: 'id',
        type: 'number',
        rules: [{ required: true }],
        editable: false,
    },
    'request_type': {
        title: 'Request Type',
        dataIndex: 'request_type',
        type: 'string',
        rules: [{ required: true }],
        editable: true,
    },
    'requester': {
        title: 'Requester',
        dataIndex: 'requester',
        type: 'string',
        rules: [{ required: true }],
        editable: true,
    },
    'status': {
        title: 'Status',
        dataIndex: 'status',
        type: 'select',
        options: [
            { value: 'Pending', label: 'Pending' },
            { value: 'Approved', label: 'Approved' },
            { value: 'Rejected', label: 'Rejected' },
        ],
        rules: [{ required: true }],
        editable: true,
    },
    'submitted_date': {
        title: 'Submitted Date',
        dataIndex: 'submitted_date',
        type: 'date',
        rules: [{ required: true }],
        editable: true,
    },
    'document_url': {
        title: 'Document URL',
        dataIndex: 'document_url',
        type: 'string',
        editable: true,
    },
    'comments': {
        title: 'Comments',
        dataIndex: 'comments',
        type: 'textarea',
        editable: true,
    }
};

// 定义表单字段
const CertifyRequestFormFields = (formRef) => [
    {
        name: 'request_type',
        label: 'Request Type',
        type: 'string',
        rules: [{ required: true, message: 'Please input the request type!' }],
    },
    {
        name: 'requester',
        label: 'Requester',
        type: 'string',
        rules: [{ required: true, message: 'Please input the requester!' }],
    },
    {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
            { value: 'Pending', label: 'Pending' },
            { value: 'Approved', label: 'Approved' },
            { value: 'Rejected', label: 'Rejected' },
        ],
        rules: [{ required: true, message: 'Please select a status!' }],
    },
    {
        name: 'submitted_date',
        label: 'Submitted Date',
        type: 'date',
        rules: [{ required: true, message: 'Please select a submitted date!' }],
    },
    {
        name: 'document_url',
        label: 'Document URL',
        type: 'string',
    },
    {
        name: 'comments',
        label: 'Comments',
        type: 'textarea',
    },
];

// 定义相关字段
const CertifyRequestRelated = {};

class CertifyRequestComponent extends Component {
    actionRef = React.createRef();
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();
        this.state = {
            data: [],
            isLoading: false,
            tableRef: null,
            filtered_startDate: null,
            filtered_endDate: null,
            filterCompletedWorkorder: false,
            status: null,
        };
    }

    // 将 columns 放在 class 内部
    getColumns = () => {
        return [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
                sorter: true,
            },
            {
                title: 'facility_name',
                dataIndex: 'facility_name',
                key: 'facility_name',
                sorter: true,
            },

            {
                title: 'type',
                dataIndex: 'type_display',
                key: 'type_display',
                sorter: true,
            },
            {
                title: 'Request By',
                dataIndex: 'request_by_name',
                key: 'request_by_name',
                sorter: true,
            },
            {
                title: 'Assigner',
                dataIndex: 'received_by_name',
                key: 'received_by_name',
                sorter: true,
            },
            {
                title: 'Assign To',
                dataIndex: 'certify_by_name',
                key: 'certify_by_name',
                sorter: true,
            },
            {
                title: 'Modified Date',
                dataIndex: 'modified_date',
                key: 'modified_date',
                sorter: true,
                render: (text, record) => (
                    moment(record.modified_date).format('YYYY-MM-DD')
                )
            },

            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                editable: true,
                sorter: true,
            },
            {
                title: 'Action',
                width: 200,
                render: (text, record) => (
                    <>
                        <AssignButton
                            taskId={record.id}
                            facilityId={record.facility}
                            onAssignSuccess={() => this.refresh(true)}
                        />
                        <Popconfirm
                            title="Are you sure you want to delete this task?"
                            onConfirm={() => this.handleDeleteCertifyRequest(record.id)}
                        >
                            <a style={{ color: '#ff4d4f', cursor: 'pointer', marginLeft: '8px' }}>Delete</a>
                        </Popconfirm>
                    </>
                )
            }
        ];
    };

    handleCreateCertifyRequest = (body) => {
        // 实际应用中替换为真实API调用
        return api.createCertifyRequest(body).then(
            response => {
                return response.data;
            }
        ).catch(error => (
            error
        ));
    }

    handleDeleteCertifyRequest = (id) => {
        // 实际应用中替换为真实API调用
        return api.deleteCertifyRequest(id).then(
            response => {
                this.refresh(true); // 删除成功后刷新表格
                return response.data;
            }
        ).catch(error => (
            error
        ));
    }

    handleBulkInsert = (body) => {
        // 实际应用中替换为真实API调用
        return api.bulkCertifyRequestInsert(body).then(
            response => {
                return response.data;
            }
        ).catch(error => {
            return error;
        });
    }

    handleBulkDelete = (body) => {
        // 实际应用中替换为真实API调用
        
        return api.bulkDeleteCertifyRequest(body.map(item => ({id: item.id}))).then(
            response => {
                return response.data;
            }
        ).catch(error => (
            error
        ));
    }

    handOnlineSave = (key, row) => {
        if (key < 0) {
            return this.handleCreateCertifyRequest(row);
        } else {
            // 实际应用中替换为真实API调用
            return api.editCertifyRequest(key, row).then(
                response => {
                    return response;
                }
            ).catch(error => (
                error
            ));
        }
    }

    handleFilterCompleteWorkorder = (e) => {
        if (e.target.checked) {
            this.setState({
                filterCompletedWorkorder: true,
                status: "Submitted,Pending,Received,Updated,Rejected"
            }, () => {
                this.actionRef.current.reset();
            });
        } else {
            this.setState({
                filterCompletedWorkorder: false,
                status: null
            }, () => {
                this.actionRef.current.reset();
            });
        }
    };

    handleFilter = (query = {}) => {
        this.setState({ isLoading: true });

        // 如果query是字符串，先解析成对象
        const baseQuery = typeof query === 'string' ?
            Object.fromEntries(new URLSearchParams(query)) :
            query;

        const queryParams = {
            ...baseQuery,
            ...(this.state.filtered_startDate && { start_date: this.state.filtered_startDate }),
            ...(this.state.filtered_endDate && { end_date: this.state.filtered_endDate }),
            ...(this.state.status && { status__in: this.state.status })
        };

        const cleanQueryParams = Object.fromEntries(
            Object.entries(queryParams).filter(([_, value]) => value != null)
        );

        // 实际应用中替换为真实API调用
        return api.getCertifyRequests(cleanQueryParams)
            .then(
                response => {
                    let dataSource = response.data.results;
                    return {
                        data: dataSource,
                        success: true,
                        total: response.data.count
                    };
                }
            )
            .catch(error => (
                error
            ));
    }

    getCertifyRequests = (data) => {
        this.setState({
            data,
            isLoading: false
        });
        // 如果需要存储到Redux状态中
        if (this.props.setCertifyRequestSuccess) {
            this.props.setCertifyRequestSuccess(data);
        }
    }

    setFilters = (e) => {
        this.setState({
            filtered_startDate: e.startDate ? moment(e.startDate).format('YYYY-MM-DD') : null,
            filtered_endDate: e.endDate ? moment(e.endDate).format('YYYY-MM-DD') : null,
        }, () => {
            this.refresh(false);
        });
    }

    renderFilter = () => {
        return (
            <div className={"flex gap-5 items-center"}>
                <Checkbox
                    checked={this.state.filterCompletedWorkorder}
                    onChange={this.handleFilterCompleteWorkorder}
                >
                    Hide Completed
                </Checkbox>
            </div>
        )
    }

    refresh = (needRequest) => {
        return this.tableRef?.current?.refresh(needRequest);
    };

    gettableRef = (tableRef) => {
        this.setState({ tableRef });
    }

    getActionRef = (actionRef) => {
        this.actionRef.current = actionRef.current;
    }

    render() {
        return (
            <>
                <EditableTable
                    key={'CertifyRequest'}
                    ref={this.tableRef}
                    columns={this.getColumns()} // 使用类方法获取列配置
                    data={this.state.data}
                    columnProps={CertifyRequestProps}
                    formFields={CertifyRequestFormFields(this.state.tableRef)}
                    formRelated={CertifyRequestRelated}
                    getRecord={this.getCertifyRequests}
                    createRecords={this.handleCreateCertifyRequest}
                    deleteRecord={this.handleDeleteCertifyRequest}
                    bulkInsert={this.handleBulkInsert}
                    bulkDelete={this.handleBulkDelete}
                    handOnlineSave={this.handOnlineSave}
                    handleFilter={this.handleFilter}
                    renderFilter={this.renderFilter}
                    isRowClickable={false}
                    isRowHover={true}
                    hide_search={false}
                    textAlign={'table-text-left'}
                    isChat={false}
                    isRelated={false}
                    isAttachments={false}
                    getFormRef={this.gettableRef}
                    hid_new={true}
                    hide_imports={true}
                    getActionRef={this.getActionRef}
                />
            </>
        );
    }
}

// 将组件连接到Redux
const mapStateToProps = state => {
    return {
        CertifyRequests: _.get(state.CertifyRequest, 'data'),
        Selected_Rows: _.get(state.EditableTable, 'selected_rows'),
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setCertifyRequestSuccess: (e) => {
            // 如果有对应的Redux action，取消注释以下代码
            // dispatch(actions.CertifyRequestSuccess(e))
        },
        SelectedRows: (ids) => dispatch(EditableTableActions.SelectedRows(ids))
    };
};

const withHooks = (Component) => {
    const NewComponent = (props) => {

        const { refreshTab } = useKeepAliveContext()

        useEventBus(EventName.REFRESH_CERTIFY_REQUEST, () => {
            refreshTab('/certifyRequest')
        })

        return (
            <Component
                {...props}
            />
        )
    }
    return NewComponent
}

export const CertifyRequest = withHooks(connect(
    mapStateToProps,
    mapDispatchToProps
)(CertifyRequestComponent));